import type { FeedItem } from '@knocklabs/client';
import { create } from 'zustand';

// Narrow view of Knock's feed client — only the members the app consumes. The
// real Knock `Feed` is structurally assignable to this, and the no-op default
// (used when Knock isn't configured, e.g. ephemeral/preview envs) satisfies it.
interface MessagesFeedClient {
  fetch: () => void;
  markAsRead: (item: FeedItem) => void;
  markAsArchived: (item: FeedItem) => void;
  markAllAsArchived: () => Promise<unknown>;
}

const noopClient: MessagesFeedClient = {
  fetch: () => undefined,
  markAsRead: () => undefined,
  markAsArchived: () => undefined,
  markAllAsArchived: () => Promise.resolve(),
};

interface MessagesFeedState {
  items: FeedItem[];
  loading: boolean;
  client: MessagesFeedClient;
  // Mirror the live Knock feed into the store (called by the bridge).
  setFeed: (feed: {
    items: FeedItem[];
    loading: boolean;
    client: MessagesFeedClient;
  }) => void;
  // Reset to an inert empty feed when Knock isn't configured.
  reset: () => void;
  fetch: () => void;
  markAsRead: (item: FeedItem) => void;
  markAsArchived: (item: FeedItem) => void;
  markAllAsArchived: () => Promise<unknown>;
}

export const useMessagesFeedStore = create<MessagesFeedState>((set, get) => ({
  items: [],
  loading: false,
  client: noopClient,
  setFeed: ({ items, loading, client }) => set({ items, loading, client }),
  reset: () => set({ items: [], loading: false, client: noopClient }),
  fetch: () => get().client.fetch(),
  markAsRead: (item) => get().client.markAsRead(item),
  markAsArchived: (item) => get().client.markAsArchived(item),
  markAllAsArchived: () => get().client.markAllAsArchived(),
}));
